import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import * as mui from '@mui/material';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import BusinessIcon from '@mui/icons-material/Business';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DnsIcon from '@mui/icons-material/Dns';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TuneIcon from '@mui/icons-material/Tune';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ResponsiveContainer from '../../layout/Container';
import DynamicTabs from '../../common/DynamicTabs';
import HomeIcon from '@mui/icons-material/Home';
import NetworkService from '../../../services/NetworkService';

// Import split components
import { 
  DashboardCard, 
  SectionHeader, 
  StatsOverview 
} from './NetworkComponents/UIComponents';
import NetworkDashboard from './NetworkComponents/NetworkDashboard';
import LDAPConfiguration from './NetworkComponents/LDAPConfiguration';
import DomainManagement from './NetworkComponents/DomainManagement';
import JobScheduling from './NetworkComponents/JobScheduling';
import TerminatedUserAnalysis from './NetworkComponents/TerminatedUserAnalysis';
import JobHistory from './NetworkComponents/JobHistory';
import ReportsView from './NetworkComponents/ReportsView';
import NetworkAdmins from './NetworkComponents/NetworkAdmins';
import Notification from '../../common/Notification';

// Custom breadcrumb component
const NetworkBreadcrumbs = ({ activeView, viewTitle, changeView }) => {
  return (
    <mui.Breadcrumbs aria-label="breadcrumb">
      <mui.Link underline="hover" color="inherit" href="/dashboard">
        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
          Home
      </mui.Box>
      </mui.Link>
      <mui.Link underline="hover" color="inherit" href="/dashboard">
        Dashboard
      </mui.Link>
      <mui.Link underline="hover" color="inherit" href="/dashboard">
        System Settings
      </mui.Link>
      <mui.Link 
            underline="hover" 
            color="inherit" 
            sx={{ cursor: 'pointer' }}
            onClick={() => changeView('dashboard', 'Network Layer Management')}
          >
            Network Layer Management
          </mui.Link>
     
    </mui.Breadcrumbs>
  );
};

const NetworkLayer = () => {
    // State for the active view and navigation
    const [activeView, setActiveView] = useState('dashboard');
    const [viewTitle, setViewTitle] = useState('Network Layer Management');

    // State for notification alerts
    const [showAlert, setShowAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');

    // Shared data states
    const [ldap, setLDAP] = useState({
        LDAP_SERVER: '',
        LDAP_PORT: '',
        BASE_DN: '',
        BIND_DN: '',
        LDAP_PW_HASHED: '',
        DOMAIN: '',
        AUTO_TERMINATE_AD: false
    });
    const [ldapExist, setLDAPExist] = useState(false);
    const [ldapID, setLDAPID] = useState('');
    const [domains, setDomains] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [terminatedUsers, setTerminatedUsers] = useState([]);
    const [networkJobLogs, setNetworkJobLogs] = useState([]);

    // Change to new view and update title
    const changeView = (view, title) => {
        setActiveView(view);
        setViewTitle(title);
    };

    useEffect(() => {
        fetchNetworkAuth();
        fetchNetworkDomains();
        fetchNetworkJobs();
        fetchNetworkJobLogs();
    }, []);

        const fetchNetworkAuth = async () => {
            try {
            const response = await NetworkService.fetchNetworkAuth();
                if (response && response.length > 0) {
                setLDAP(response[0]);
                setLDAPID(response[0].id);
                setLDAPExist(true);
            }
            } catch (error) {
                console.error('Error fetching LDAP data:', error);
            }
        };

    const fetchNetworkDomains = async () => {
        try {
            const response = await NetworkService.fetchNetworkDomains();
            if (response) {
                // Normalize domain data to ensure consistent ID types
                const normalizedDomains = response.map(domain => ({
                    ...domain,
                    id: domain.id ? String(domain.id) : null
                }));
                setDomains(normalizedDomains);
            }
        } catch (error) {
            console.error('Error fetching network domains:', error);
        }
    };

    const fetchNetworkJobs = async () => {
        try {
            const response = await NetworkService.fetchNetworkJobs();
            
            if (response) {
                // Normalize job data
                const normalizedJobs = response
                    .filter(job => job && job.id) // Filter out invalid records
                    .map(job => ({
                            ...job,
                            // Preserve original DOMAIN_ID but ensure it's not undefined
                            DOMAIN_ID: job.DOMAIN_ID != null ? job.DOMAIN_ID : null,
                            // Preserve original SCHEDULE_TIME but ensure it's not undefined
                            SCHEDULE_TIME: job.SCHEDULE_TIME != null ? job.SCHEDULE_TIME : null,
                            // Ensure DAYS_OF_WEEK is an array
                            DAYS_OF_WEEK: Array.isArray(job.DAYS_OF_WEEK) ? job.DAYS_OF_WEEK : 
                                        (job.DAYS_OF_WEEK ? job.DAYS_OF_WEEK.split(',') : []),
                            // Ensure ENABLED is a boolean
                            ENABLED: Boolean(job.ENABLED)
                    }));
                
                setJobs(normalizedJobs);
            } else {
                setJobs([]);
            }
        } catch (error) {
            console.error('Error fetching network jobs:', error);
            setJobs([]);
        }
    };

    const fetchNetworkJobLogs = async () => {
        try {
            const response = await NetworkService.fetchNetworkJobLogs();
            if (response) {
                setNetworkJobLogs(response);
            }
        } catch (error) {
            console.error('Error fetching network job logs:', error);
        }
    };

    const fetchTerminatedUserAnalysis = async (analysisFilter) => {
        try {
            const response = await NetworkService.fetchTerminatedUserAnalysis(analysisFilter);
            if (response) {
                setTerminatedUsers(response);
                return response;
            }
            return [];
        } catch (error) {
            console.error('Error fetching terminated user analysis:', error);
            return [];
        }
    };

    const testDomainConnection = async (domainId, password = '') => {
        try {
            // Show pending state
            setAlertTitle('Testing Connection');
            setAlertMessage('Connecting to domain...');
            setShowAlert(true);
            
            // Find domain name for better messaging
            const domain = domains.find(d => d.id === domainId);
            const domainName = domain ? domain.DOMAIN_NAME : 'domain';
            
            const credentials = { password };
            
            const response = await NetworkService.testDomainConnection(domainId, credentials);
            
            if (response && response.success) {
                setAlertTitle('Connection Successful');
                setAlertMessage(response.message || `Successfully connected to ${domainName}`);
            } else {
                setAlertTitle('Connection Failed');
                setAlertMessage(response.message || `Failed to connect to ${domainName}`);
            }
            setShowAlert(true);
            return response;
        } catch (error) {
            console.error('Error testing domain connection:', error);
            
            // Try to extract the most useful error message
            let errorMessage = '';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            } else {
                errorMessage = 'Failed to connect to the domain';
            }
            
            setAlertTitle('Connection Failed');
            setAlertMessage(errorMessage);
            setShowAlert(true);
            return { success: false, message: errorMessage };
        }
    };

    // Shared alert handler for child components
    const showNotification = (title, message) => {
        setAlertTitle(title);
        setAlertMessage(message);
            setShowAlert(true);
    };

    // Define dashboard stats
    const dashboardStats = [
        { 
            label: 'Configured Domains', 
            value: domains.length,
            icon: <DnsIcon />,
            color: 'primary.main'
        },
        { 
            label: 'Scheduled Jobs', 
            value: jobs.length,
            icon: <ScheduleIcon />,
            color: 'secondary.main'
        },
        { 
            label: 'Terminated Users at Risk', 
            value: terminatedUsers.filter(u => u.RISK_LEVEL === 'HIGH').length,
            icon: <SecurityIcon />,
            color: 'error.main'
        },
        { 
            label: 'Job Success Rate', 
            value: networkJobLogs.length ? 
                `${Math.round((networkJobLogs.filter(log => log.STATUS === 'SUCCESS').length / networkJobLogs.length) * 100)}%` : 
                'N/A',
            icon: <AssessmentIcon />,
            color: 'success.main'
        }
    ];

    // Get the icon for the current view
    const getViewIcon = () => {
        switch (activeView) {
            case 'dashboard': return <BusinessIcon />;
            case 'ldap': return <DnsIcon />;
            case 'domains': return <StorageIcon />;
            case 'jobs': return <ScheduleIcon />;
            case 'analysis': return <VerifiedUserIcon />;
            case 'logs': return <HistoryIcon />;
            case 'reports': return <AssessmentIcon />;
            case 'network-admins': return <AdminPanelSettingsIcon />;
            default: return <BusinessIcon />;
        }
    };

    const customMainContent = (
        <div className="min-h-screen bg-gray-50">
            {/* Add a global style rule to hide "0" that shows up in select elements */}
            <style jsx="true">{`
                .MuiSelect-root + div {
                    display: none !important;
                }
                .MuiSelect-root input {
                    opacity: 0 !important;
                    height: 0 !important;
                    width: 0 !important;
                    position: absolute !important;
                }
            `}</style>
            
            <ResponsiveContainer>
                <NetworkBreadcrumbs
                  activeView={activeView}
                  viewTitle={viewTitle}
                  changeView={changeView}
                />

                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                    <mui.Box sx={{ flexGrow: 1 }}>
                        <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                            <mui.Box sx={{ mr: 1, color: 'primary.main' }}>
                                {getViewIcon()}
                            </mui.Box>
                            {viewTitle}
                        </mui.Typography>
                        <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Configure network interfaces and manage automated access management
                </mui.Typography>
                    </mui.Box>
                </mui.Box>

                <Separator />

                {showAlert && <Notification title={alertTitle} message={alertMessage} onClose={() => setShowAlert(false)} />}

                {/* Dashboard View */}
                <mui.Box sx={{ 
                    backgroundColor: '#f8fafc', 
                    borderRadius: 2,
                    minHeight: '70vh',
                    mt: 3,
                    boxShadow: 'inset 0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}>
                    <mui.Paper 
                        elevation={0}
                        sx={{ 
                            p: 2,
                            height: '100%',
                            backgroundColor: 'transparent'
                        }}
                    >
                {activeView === 'dashboard' && (
                        <NetworkDashboard 
                            stats={dashboardStats}
                            domains={domains}
                            jobs={jobs}
                            terminatedUsers={terminatedUsers}
                            networkJobLogs={networkJobLogs}
                            changeView={changeView}
                        />
                    )}

                 

                {activeView === 'domains' && (
                        <DomainManagement
                            domains={domains}
                            fetchDomains={fetchNetworkDomains}
                            testDomainConnection={testDomainConnection}
                            showNotification={showNotification}
                        />
                    )}

                {activeView === 'jobs' && (
                        <JobScheduling
                            jobs={jobs}
                            domains={domains}
                            fetchJobs={fetchNetworkJobs}
                            showNotification={showNotification}
                            changeView={changeView}
                        />
                    )}

                {activeView === 'analysis' && (
                        <TerminatedUserAnalysis
                            domains={domains}
                            terminatedUsers={terminatedUsers}
                            setTerminatedUsers={setTerminatedUsers}
                            fetchAnalysis={fetchTerminatedUserAnalysis}
                            showNotification={showNotification}
                        />
                    )}

                {activeView === 'logs' && (
                        <JobHistory
                            networkJobLogs={networkJobLogs}
                            fetchLogs={fetchNetworkJobLogs}
                        />
                    )}

                {activeView === 'reports' && (
                        <ReportsView
                            changeView={changeView}
                        />
                )}

                {activeView === 'network-admins' && (
                        <NetworkAdmins
                            domains={domains}
                            showNotification={showNotification}
                            changeView={changeView}
                        />
                )}
            </mui.Paper>
        </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default NetworkLayer;
